/**
 * Subscription Service
 * 
 * Handles all subscription-related API calls and data management.
 * Provides methods for checking status, activating, deactivating, and managing subscriptions.
 * 
 * Features:
 * - Retry logic for failed operations
 * - Status caching for performance
 * - Comprehensive error handling
 * - HTTP status code handling
 */

import { apiClient } from '../../api/config/ApiClient';
import { 
  SUBSCRIPTION_STATUS, 
  SUBSCRIPTION_TYPES,
  SUBSCRIPTION_ERROR_TYPES,
  DEFAULT_SUBSCRIPTION_CONFIG
} from '../constants/subscriptionTypes';

/**
 * Subscription Service Class
 */
class SubscriptionService {
  // Cache for subscription status
  static statusCache = {
    data: null,
    timestamp: null,
    isValid: false
  };

  // Retry configuration
  static retryConfig = {
    maxAttempts: DEFAULT_SUBSCRIPTION_CONFIG.MAX_RETRY_ATTEMPTS || 3,
    retryDelay: DEFAULT_SUBSCRIPTION_CONFIG.RETRY_DELAY || 1000,
    retryableStatuses: [408, 429, 500, 502, 503, 504]
  };
  /**
   * Retry wrapper for API calls
   * @param {Function} apiCall - API call function
   * @param {number} attempt - Current attempt number
   * @returns {Promise<any>} API response
   */
  static async retryApiCall(apiCall, attempt = 1) {
    try {
      return await apiCall();
    } catch (error) {
      const status = error.response?.status;
      const isRetryable = this.retryConfig.retryableStatuses.includes(status);
      const shouldRetry = isRetryable && attempt < this.retryConfig.maxAttempts;

      if (shouldRetry) {
        const delay = this.retryConfig.retryDelay * attempt;
        console.log(`🔄 Retrying API call (attempt ${attempt + 1}/${this.retryConfig.maxAttempts}) after ${delay}ms...`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.retryApiCall(apiCall, attempt + 1);
      }

      throw error;
    }
  }

  /**
   * Check if cache is valid
   * @returns {boolean} Whether cache is valid
   */
  static isCacheValid() {
    if (!this.statusCache.isValid || !this.statusCache.timestamp) {
      return false;
    }

    const cacheAge = Date.now() - this.statusCache.timestamp;
    const maxAge = DEFAULT_SUBSCRIPTION_CONFIG.CACHE_DURATION;
    
    return cacheAge < maxAge;
  }

  /**
   * Clear subscription status cache
   */
  static clearCache() {
    this.statusCache = {
      data: null,
      timestamp: null,
      isValid: false
    };
  }

  /**
   * Update subscription status cache
   * @param {Object} data - Subscription data to cache
   */
  static updateCache(data) {
    this.statusCache = {
      data,
      timestamp: Date.now(),
      isValid: true
    };
  }

  /**
   * Check subscription status with caching
   * @param {string} userId - User ID (optional, will use current user if not provided)
   * @param {boolean} forceRefresh - Force refresh cache (default: false)
   * @returns {Promise<Object>} Subscription status data
   */
  static async checkStatus(userId = null, forceRefresh = false) {
    try {
      // Return cached data if valid and not forcing refresh
      if (!forceRefresh && this.isCacheValid()) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 SubscriptionService: Returning cached status');
        }
        return {
          data: this.statusCache.data,
          error: null
        };
      }

      const endpoint = '/auth/subscriptions/status';
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 SubscriptionService: Checking status at endpoint:', endpoint);
      }
      
      // Make API call with retry logic
      const responseData = await this.retryApiCall(async () => {
        return await apiClient.get(endpoint);
      });
      
      // Handle case where response might be wrapped in a data property
      // The interceptor might return response.data or the response itself
      const backendData = responseData?.data || responseData;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 SubscriptionService: Raw response data:', JSON.stringify(responseData, null, 2));
        console.log('🔍 SubscriptionService: Extracted backend data:', JSON.stringify(backendData, null, 2));
      }
      
      // Transform the backend response format to our internal format
      const transformedData = this.transformBackendStatusResponse(backendData);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 SubscriptionService: Transformed status data:', JSON.stringify(transformedData, null, 2));
      }
      
      // Update cache
      this.updateCache(transformedData);
      
      return {
        data: transformedData,
        error: null
      };
    } catch (error) {
      console.error('🔍 SubscriptionService: Failed to check subscription status:', error);
      
      // Return cached data if available on error
      if (this.statusCache.data) {
        console.log('🔍 SubscriptionService: Returning stale cached data due to error');
        return {
          data: this.statusCache.data,
          error: this.handleSubscriptionError(error)
        };
      }
      
      return {
        data: null,
        error: this.handleSubscriptionError(error)
      };
    }
  }

  /**
   * Activate subscription with retry logic
   * @param {string} subscriptionType - Type of subscription to activate
   * @returns {Promise<Object>} Activation result
   */
  static async activate(subscriptionType = SUBSCRIPTION_TYPES.BASIC) {
    try {
      // Make API call with retry logic
      const response = await this.retryApiCall(async () => {
        return await apiClient.post('/auth/activate-subscription', {
          type: subscriptionType
        });
      });
      
      // Enhanced backend response now includes subscription status
      // Note: ResponseInterceptor returns data directly, not in response.data
      const responseData = response.data || response;
      
      let transformedData;
      
      if (responseData.hasActiveSubscription && responseData.subscriptionValidTo) {
        // Transform the enhanced response to our internal format
        transformedData = {
          status: SUBSCRIPTION_STATUS.ACTIVE,
          type: subscriptionType,
          id: 'subscription_id', // Backend doesn't provide ID, use placeholder
          userId: 'current_user',
          activatedAt: new Date().toISOString(),
          expiresAt: responseData.subscriptionValidTo,
          renewsAt: responseData.subscriptionValidTo,
          features: DEFAULT_SUBSCRIPTION_CONFIG.FEATURES_BY_TYPE[subscriptionType] || []
        };
      } else {
        // Fallback to basic active response
        transformedData = {
          status: SUBSCRIPTION_STATUS.ACTIVE,
          type: subscriptionType,
          id: `sub_${Date.now()}`,
          userId: 'current_user',
          activatedAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          renewsAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          features: DEFAULT_SUBSCRIPTION_CONFIG.FEATURES_BY_TYPE[subscriptionType] || []
        };
      }
      
      // Update cache with new subscription status
      this.updateCache(transformedData);
      
      return {
        data: transformedData,
        error: null
      };
    } catch (error) {
      return {
        data: null,
        error: this.handleSubscriptionError(error)
      };
    }
  }

  /**
   * Deactivate subscription
   * @param {string} subscriptionId - Subscription ID
   * @returns {Promise<Object>} Deactivation result
   */
  static async deactivate(subscriptionId) {
    try {
      const response = await apiClient.post(`/subscriptions/${subscriptionId}/deactivate`);
      
      return {
        data: this.transformSubscriptionData(response.data),
        error: null
      };
    } catch (error) {
      console.error('Failed to deactivate subscription:', error);
      return {
        data: null,
        error: this.handleSubscriptionError(error)
      };
    }
  }

  /**
   * Renew subscription
   * @param {string} subscriptionId - Subscription ID
   * @returns {Promise<Object>} Renewal result
   */
  static async renew(subscriptionId) {
    try {
      const response = await apiClient.post(`/subscriptions/${subscriptionId}/renew`);
      
      return {
        data: this.transformSubscriptionData(response.data),
        error: null
      };
    } catch (error) {
      console.error('Failed to renew subscription:', error);
      return {
        data: null,
        error: this.handleSubscriptionError(error)
      };
    }
  }

  /**
   * Cancel subscription with retry logic
   * @param {string} subscriptionId - Subscription ID
   * @returns {Promise<Object>} Cancellation result
   */
  static async cancel(subscriptionId) {
    try {
      // Make API call with retry logic
      const response = await this.retryApiCall(async () => {
        return await apiClient.post('/auth/cancel-subscription');
      });
      
      // Enhanced backend response now includes subscription status
      // Note: ResponseInterceptor returns data directly, not in response.data
      const responseData = response.data || response;
      
      let transformedData;
      
      if (responseData.hasActiveSubscription === false) {
        // Transform the enhanced response to our internal format
        transformedData = {
          status: SUBSCRIPTION_STATUS.INACTIVE, // Use INACTIVE instead of CANCELLED for consistency
          type: null,
          id: null,
          userId: 'current_user',
          activatedAt: null,
          expiresAt: responseData.subscriptionValidTo, // Will be null for cancelled subscription
          renewsAt: responseData.subscriptionValidTo, // Will be null for cancelled subscription
          features: []
        };
      } else {
        // Fallback to basic cancelled response
        transformedData = {
          status: SUBSCRIPTION_STATUS.INACTIVE,
          type: null,
          id: null,
          userId: 'current_user',
          activatedAt: null,
          expiresAt: null,
          renewsAt: null,
          features: []
        };
      }
      
      // Clear cache after cancellation
      this.clearCache();
      
      return {
        data: transformedData,
        error: null
      };
    } catch (error) {
      return {
        data: null,
        error: this.handleSubscriptionError(error)
      };
    }
  }

  /**
   * Get subscription history
   * @param {string} userId - User ID (optional)
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Subscription history
   */
  static async getHistory(userId = null, params = {}) {
    try {
      const endpoint = userId ? `/subscriptions/history/${userId}` : '/subscriptions/history';
      const response = await apiClient.get(endpoint, { params });
      
      return {
        data: response.data.map(item => this.transformSubscriptionData(item)),
        error: null
      };
    } catch (error) {
      console.error('Failed to get subscription history:', error);
      return {
        data: null,
        error: this.handleSubscriptionError(error)
      };
    }
  }

  /**
   * Get subscription features
   * @param {string} subscriptionType - Subscription type
   * @returns {Promise<Object>} Subscription features
   */
  static async getFeatures(subscriptionType) {
    try {
      const response = await apiClient.get(`/subscriptions/features/${subscriptionType}`);
      
      return {
        data: response.data,
        error: null
      };
    } catch (error) {
      console.error('Failed to get subscription features:', error);
      return {
        data: DEFAULT_SUBSCRIPTION_CONFIG.FEATURES_BY_TYPE[subscriptionType] || [],
        error: this.handleSubscriptionError(error)
      };
    }
  }

  /**
   * Validate subscription access
   * @param {string} feature - Feature to validate
   * @returns {Promise<Object>} Validation result
   */
  static async validateAccess(feature) {
    try {
      const response = await apiClient.get(`/subscriptions/validate/${feature}`);
      
      return {
        data: response.data,
        error: null
      };
    } catch (error) {
      console.error('Failed to validate subscription access:', error);
      return {
        data: false,
        error: this.handleSubscriptionError(error)
      };
    }
  }

  /**
   * Transform backend status response to internal format
   * @param {Object} backendData - Backend response data
   * @returns {Object} Transformed subscription data
   */
  static transformBackendStatusResponse(backendData) {
    if (!backendData) {
      return {
        status: SUBSCRIPTION_STATUS.NONE,
        type: null,
        id: null,
        userId: null,
        activatedAt: null,
        expiresAt: null,
        renewsAt: null,
        features: []
      };
    }

    const hasActiveSubscription = backendData.hasActiveSubscription;
    const subscriptionValidTo = backendData.subscriptionValidTo;

    // Warn if subscription is active but subscriptionValidTo is missing (dev only)
    if (process.env.NODE_ENV === 'development' && hasActiveSubscription && !subscriptionValidTo) {
      console.warn('⚠️ SubscriptionService: Backend returned active subscription but subscriptionValidTo is null. This is a backend issue - the subscription expiry date should be provided.');
    }

    // Calculate activated date as expiry date minus 1 month
    let activatedAt = null;
    if (hasActiveSubscription && subscriptionValidTo) {
      try {
        const expiryDate = new Date(subscriptionValidTo);
        if (isNaN(expiryDate.getTime())) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ SubscriptionService: Invalid subscriptionValidTo date format:', subscriptionValidTo);
          }
        } else {
          const activationDate = new Date(expiryDate);
          activationDate.setMonth(activationDate.getMonth() - 1);
          activatedAt = activationDate.toISOString();
        }
      } catch (error) {
        // Always log errors, even in production, as they indicate actual problems
        console.error('⚠️ SubscriptionService: Error parsing subscriptionValidTo date:', error);
        activatedAt = null;
      }
    }

    return {
      status: hasActiveSubscription ? SUBSCRIPTION_STATUS.ACTIVE : SUBSCRIPTION_STATUS.INACTIVE,
      type: hasActiveSubscription ? SUBSCRIPTION_TYPES.BASIC : null,
      id: hasActiveSubscription ? `sub_${Date.now()}` : null,
      userId: 'current_user',
      email: backendData.email || null,
      activatedAt: activatedAt,
      expiresAt: subscriptionValidTo || null,
      renewsAt: subscriptionValidTo || null,
      features: hasActiveSubscription ? (DEFAULT_SUBSCRIPTION_CONFIG.FEATURES_BY_TYPE[SUBSCRIPTION_TYPES.BASIC] || []) : [],
      tier: backendData.tier || 'PREMIUM',
      nearArbEnabled: Boolean(backendData.nearArbEnabled),
    };
  }

  /**
   * Transform subscription data from API response
   * @param {Object} apiData - Raw API data
   * @returns {Object} Transformed subscription data
   */
  static transformSubscriptionData(apiData) {
    if (!apiData) {
      return {
        status: SUBSCRIPTION_STATUS.NONE,
        type: null,
        id: null,
        userId: null,
        activatedAt: null,
        expiresAt: null,
        renewsAt: null,
        features: []
      };
    }

    return {
      status: this.mapApiStatusToSubscriptionStatus(apiData.status),
      type: apiData.type || SUBSCRIPTION_TYPES.BASIC,
      id: apiData.id,
      userId: apiData.userId,
      activatedAt: apiData.activatedAt,
      expiresAt: apiData.expiresAt,
      renewsAt: apiData.renewsAt,
      features: apiData.features || []
    };
  }

  /**
   * Map API status to subscription status
   * @param {string} apiStatus - API status string
   * @returns {string} Mapped subscription status
   */
  static mapApiStatusToSubscriptionStatus(apiStatus) {
    const statusMap = {
      'active': SUBSCRIPTION_STATUS.ACTIVE,
      'inactive': SUBSCRIPTION_STATUS.INACTIVE,
      'expired': SUBSCRIPTION_STATUS.EXPIRED,
      'suspended': SUBSCRIPTION_STATUS.SUSPENDED,
      'cancelled': SUBSCRIPTION_STATUS.CANCELLED,
      'none': SUBSCRIPTION_STATUS.NONE
    };

    return statusMap[apiStatus] || SUBSCRIPTION_STATUS.NONE;
  }

  /**
   * Handle subscription errors
   * @param {Error} error - Error object
   * @returns {Object} Standardized error object
   */
  static handleSubscriptionError(error) {
    const errorResponse = error.response;
    
    if (!errorResponse) {
      return {
        type: SUBSCRIPTION_ERROR_TYPES.NETWORK_ERROR,
        message: 'Network error. Please check your connection.',
        details: error.message
      };
    }

    const status = errorResponse.status;
    const errorData = errorResponse.data;

    switch (status) {
      case 400:
        return {
          type: SUBSCRIPTION_ERROR_TYPES.VALIDATION_ERROR,
          message: errorData.message || 'Invalid request data',
          details: errorData.details
        };

      case 401:
        return {
          type: SUBSCRIPTION_ERROR_TYPES.AUTH_ERROR,
          message: 'Authentication required',
          details: errorData
        };

      case 402:
        return {
          type: SUBSCRIPTION_ERROR_TYPES.PAYMENT_REQUIRED,
          message: 'Payment required for this operation',
          details: errorData
        };

      case 403:
        return {
          type: SUBSCRIPTION_ERROR_TYPES.AUTH_ERROR,
          message: 'Access denied',
          details: errorData
        };

      case 404:
        return {
          type: SUBSCRIPTION_ERROR_TYPES.NOT_FOUND,
          message: 'Subscription not found',
          details: errorData
        };

      case 409:
        return {
          type: SUBSCRIPTION_ERROR_TYPES.ALREADY_ACTIVE,
          message: 'Subscription is already active',
          details: errorData
        };

      case 500:
        return {
          type: SUBSCRIPTION_ERROR_TYPES.SERVER_ERROR,
          message: 'Server error. Please try again later.',
          details: errorData
        };

      default:
        return {
          type: SUBSCRIPTION_ERROR_TYPES.UNKNOWN,
          message: errorData.message || 'An unknown error occurred',
          details: errorData
        };
    }
  }

  /**
   * Check if subscription is valid for feature access
   * @param {Object} subscription - Subscription data
   * @param {string} feature - Feature to check
   * @returns {boolean} Whether subscription is valid for feature
   */
  static isSubscriptionValidForFeature(subscription, feature) {
    if (!subscription || subscription.status !== SUBSCRIPTION_STATUS.ACTIVE) {
      return false;
    }

    if (!subscription.features || !Array.isArray(subscription.features)) {
      return false;
    }

    return subscription.features.includes(feature);
  }

  /**
   * Get subscription expiry information
   * @param {Object} subscription - Subscription data
   * @returns {Object} Expiry information
   */
  static getSubscriptionExpiryInfo(subscription) {
    if (!subscription || !subscription.expiresAt) {
      return {
        isExpired: false,
        daysUntilExpiry: null,
        isInGracePeriod: false,
        needsRenewal: false
      };
    }

    const now = new Date();
    const expiryDate = new Date(subscription.expiresAt);
    const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    const isExpired = daysUntilExpiry <= 0;
    const isInGracePeriod = isExpired && daysUntilExpiry >= -7;
    const needsRenewal = isExpired || daysUntilExpiry <= 7;

    return {
      isExpired,
      daysUntilExpiry,
      isInGracePeriod,
      needsRenewal
    };
  }
}

export default SubscriptionService;
